import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  LogoutOutlined,
  DnsOutlined,
  SecurityOutlined,
  History as HistoryIcon,
  Home as HomeIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useAuth } from '../../features/auth';

const drawerWidth = 260;

const Sidebar = ({ mobileOpen, onDrawerToggle, window }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const container = window !== undefined ? () => window().document.body : undefined;

  const menuItems = [
    { key: 'Home', text: 'Trang chủ', icon: <HomeIcon />, path: '/home' },
    { key: 'host-list', text: 'Quản lý Host GPT', icon: <DnsOutlined />, path: '/hosts' },
    { key: 'deposit', text: 'Nạp tiền', icon: <AccountBalanceWalletIcon />, path: '/deposit' },
    { key: 'order-history', text: 'Lịch sử mua hàng', icon: <HistoryIcon />, path: '/orders' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#0A1929',
        color: '#FFFFFF',
        position: 'relative',
      }}
    >
      <Box sx={{ py: 2 }} />

      <Box sx={{ py: 1 }} />

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, px: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => {
                  navigate(item.path);
                  if (mobileOpen && onDrawerToggle) onDrawerToggle();
                }}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  justifyContent: 'initial',
                  py: 1.25,
                  minHeight: 44,
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 217, 255, 0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 217, 255, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 217, 255, 0.16)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#00D9FF',
                    },
                    '& .MuiListItemText-primary': {
                      color: '#FFFFFF',
                      fontWeight: 600,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '8px',
                      bottom: '8px',
                      width: '3px',
                      borderRadius: '0 2px 2px 0',
                      bgcolor: '#00D9FF',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? '#00D9FF' : '#B2BAC2',
                    minWidth: 36,
                    mr: 'auto',
                    justifyContent: 'center',
                    transition: 'color 0.2s ease',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: isActive ? '#FFFFFF' : '#B2BAC2',
                    transition: 'all 0.2s ease',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Divider before footer actions */}
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />

      {/* Admin Dashboard Link */}
      {user?.role === 'admin' && (
        <Button
          variant="outlined"
          startIcon={<SecurityOutlined />}
          onClick={() => navigate('/admin')}
          sx={{
            mx: 1,
            mb: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 13,
            py: 1,
            borderColor: 'rgba(255, 255, 255, 0.2)',
            color: '#90CAF9',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#00D9FF',
              backgroundColor: 'rgba(0, 217, 255, 0.08)',
              color: '#00D9FF',
            },
          }}
        >
          Admin Dashboard
        </Button>
      )}

      {/* Logout Button - Show only if logged in */}
      {user && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<LogoutOutlined />}
            onClick={handleLogout}
            sx={{
              borderRadius: '12px', // Đồng bộ với theme.shape.borderRadius
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              py: 1.25,
              color: '#FF5252',
              borderColor: 'rgba(255, 82, 82, 0.3)',
              bgcolor: 'rgba(255, 82, 82, 0.02)',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 82, 82, 0.08)',
                borderColor: '#FF5252',
                color: '#FF5252',
                boxShadow: '0 0 15px rgba(255, 82, 82, 0.15)',
                transform: 'translateY(-1px)',
              },
              '& .MuiButton-startIcon': {
                color: 'inherit',
              }
            }}
          >
            Đăng xuất
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
      {/* Mobile Drawer */}
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            bgcolor: '#0A1929',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth, // Constant width
            bgcolor: '#0A1929',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            pt: '64px', // Space for Navbar
            transition: 'width 0.2s ease',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
